import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import CapitalAllocationProposalForm from '../components/CapitalAllocationProposalForm'
import {
    createCapitalAllocationProposal,
    createCapitalAllocationProposalRevision,
    fetchCapitalAllocationProposal,
    fetchCapitalAllocationProposalHistory,
    transitionCapitalAllocationProposal,
    updateCapitalAllocationProposal
} from '../services/capitalAllocationProposalApi'

const emptyProposal = () => ({
    title: '',
    proposal_type: '',
    management_owner: '',
    executive_summary: '',
    business_objective: '',
    risk_exposure_reference: { exposure_description: '', source_reference: '', measurement_date: '', analysis_reference: '' },
    management_position: { position: 'RECOMMENDATION', selected_option: '', alternative_reference: '' },
    selection_rationale: '',
    requested_allocation: { total_capital: '', currency: '', allocation_type: '', planning_period: '', allocation_scope: '' },
    rationale: '',
    expected_outcomes: {
        resilience_objectives: [''],
        risk_reduction_reference: '',
        financial_impact_reference: '',
        expected_improvement: '',
        measurement_horizon: '',
        accountable_owner: ''
    },
    decision_center_reference: '',
    evidence_manifest: [{ type: '', source_id: '', version: '', label: '', generated_at: '' }]
})

const editablePayload = (proposal) => ({
    title: proposal.title,
    proposal_type: proposal.proposal_type,
    management_owner: proposal.management_owner,
    executive_summary: proposal.executive_summary,
    business_objective: proposal.business_objective,
    risk_exposure_reference: proposal.risk_exposure_reference,
    management_position: proposal.management_position,
    selection_rationale: proposal.selection_rationale,
    requested_allocation: {
        ...proposal.requested_allocation,
        total_capital: Number(proposal.requested_allocation.total_capital)
    },
    rationale: proposal.rationale,
    expected_outcomes: {
        ...proposal.expected_outcomes,
        resilience_objectives: proposal.expected_outcomes.resilience_objectives
            .map((objective) => objective.trim())
            .filter(Boolean)
    },
    decision_center_reference: proposal.decision_center_reference,
    evidence_manifest: proposal.evidence_manifest
})

const errorMessage = (error) => {
    const detail = error?.response?.data
    if (typeof detail === 'string') return detail
    if (detail?.detail) return detail.detail
    if (detail && typeof detail === 'object') {
        return Object.entries(detail)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(' ') : messages}`)
            .join(' ')
    }
    return error?.message || 'The proposal request could not be completed.'
}

const statusStyles = {
    DRAFT: 'bg-amber-100 text-amber-800',
    READY_FOR_REVIEW: 'bg-sky-100 text-sky-800',
    SUBMITTED: 'bg-emerald-100 text-emerald-800',
    ARCHIVED: 'bg-slate-200 text-slate-700'
}

export default function CapitalAllocationProposalWorkspace() {
    const { proposalId, version } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const isCreateMode = !proposalId
    const [proposal, setProposal] = useState(emptyProposal)
    const [metadata, setMetadata] = useState(null)
    const [loading, setLoading] = useState(!isCreateMode)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [history, setHistory] = useState([])

    useEffect(() => {
        if (isCreateMode) {
            setProposal(emptyProposal())
            setMetadata(null)
            setLoading(false)
            setError('')
            return
        }

        let active = true
        setLoading(true)
        setError('')
        fetchCapitalAllocationProposal({ proposalId, version })
            .then((data) => {
                if (!active) return
                setProposal(editablePayload(data))
                setMetadata(data)
                return fetchCapitalAllocationProposalHistory({ proposalId })
            })
            .then((versions) => active && versions && setHistory(versions))
            .catch((requestError) => active && setError(errorMessage(requestError)))
            .finally(() => active && setLoading(false))
        return () => {
            active = false
        }
    }, [isCreateMode, proposalId, version])

    const runRequest = async (request) => {
        setSaving(true)
        setError('')
        try {
            return await request()
        } catch (requestError) {
            setError(errorMessage(requestError))
            return null
        } finally {
            setSaving(false)
        }
    }

    const save = async (event) => {
        event.preventDefault()
        const payload = editablePayload(proposal)
        if (!payload.expected_outcomes.resilience_objectives.length) {
            setError('Add at least one resilience objective.')
            return
        }

        if (isCreateMode) {
            const created = await runRequest(() => createCapitalAllocationProposal(payload))
            if (created) {
                navigate(`/capital-allocation-proposals/${created.proposal_id}/versions/${created.version}`, {
                    replace: true
                })
            }
            return
        }

        const updated = await runRequest(() => updateCapitalAllocationProposal({ proposalId, version, payload }))
        if (updated) {
            setProposal(editablePayload(updated))
            setMetadata(updated)
        }
    }

    const transition = async (status) => {
        const updated = await runRequest(() => transitionCapitalAllocationProposal({ proposalId, version, status }))
        if (updated) {
            setProposal(editablePayload(updated))
            setMetadata(updated)
        }
    }

    const revise = async () => {
        const revisionReason = window.prompt('Why is a new proposal version required?')
        if (!revisionReason?.trim()) return
        const revision = await runRequest(() => createCapitalAllocationProposalRevision({
            proposalId, version, revisionReason: revisionReason.trim()
        }))
        if (revision) navigate(`/capital-allocation-proposals/${revision.proposal_id}/versions/${revision.version}`)
    }

    const status = metadata?.status || 'DRAFT'
    const readOnly = !isCreateMode && status !== 'DRAFT'
    const isSubmitted = !isCreateMode && status === 'SUBMITTED'
    const boardReviewPath = metadata
        ? `/board-decisions/proposals/${encodeURIComponent(metadata.proposal_id)}/versions/${encodeURIComponent(metadata.version)}/review`
        : ''

    if (loading) {
        return <div className="p-8 text-sm text-slate-600">Loading capital allocation proposal…</div>
    }

    return (
        <main className="min-h-full bg-slate-50 px-4 py-8 sm:px-8">
            <div className="mx-auto max-w-6xl">
                <header className="mb-6 rounded-xl border border-slate-200 bg-slate-900 p-6 text-white shadow-sm">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-widest text-sky-300">
                                Management governance · Capital allocation proposal
                            </p>
                            <h1 className="mt-2 text-2xl font-semibold">
                                {proposal.title || 'Prepare a capital allocation proposal'}
                            </h1>
                            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
                                <span>
                                    Proposal ID:{' '}
                                    <strong className="font-medium text-white">
                                        {metadata?.proposal_id || 'Assigned on creation'}
                                    </strong>
                                </span>
                                <span>
                                    Version:{' '}
                                    <strong className="font-medium text-white">
                                        {metadata?.version || 'Assigned on creation'}
                                    </strong>
                                </span>
                                <span>
                                    Owner:{' '}
                                    <strong className="font-medium text-white">
                                        {proposal.management_owner || 'Not yet assigned'}
                                    </strong>
                                </span>
                            </div>
                        </div>
                        <span
                            className={`rounded-full px-3 py-1 text-xs font-bold tracking-wide ${
                                statusStyles[status] || statusStyles.DRAFT
                            }`}
                        >
                            {status.replaceAll('_', ' ')}
                        </span>
                    </div>
                </header>

                {location.state?.message && (
                    <div className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-medium text-emerald-900">
                        {location.state.message}
                    </div>
                )}

                {isSubmitted && (
                    <section className="mb-5 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-950" aria-labelledby="board-handoff-heading">
                        <h2 id="board-handoff-heading" className="text-base font-semibold">
                            Proposal submitted for Board Decision.
                        </h2>
                        <p className="mt-2">Governance ownership has transferred to the Board.</p>
                        <p className="mt-1">Management can no longer edit this Proposal version.</p>
                        <p className="mt-1">
                            The Board may now review the submitted Proposal and record its decision.
                        </p>
                    </section>
                )}

                {error && (
                    <div
                        role="alert"
                        className="mb-5 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                    >
                        {error}
                    </div>
                )}

                <form onSubmit={save}>
                    <CapitalAllocationProposalForm
                        value={proposal}
                        onChange={setProposal}
                        disabled={readOnly || saving}
                    />

                    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        {(status === 'SUBMITTED' || status === 'ARCHIVED') && (
                            <p className="mr-auto text-sm font-medium text-slate-600">
                                This proposal version is read-only.
                            </p>
                        )}
                        {isSubmitted && (
                            <>
                                <button
                                    type="button"
                                    disabled={saving || !boardReviewPath}
                                    onClick={() => navigate(boardReviewPath)}
                                    className="rounded-lg bg-indigo-700 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-800 disabled:opacity-50"
                                >
                                    Open Board Review
                                </button>
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => navigate('/board-decisions')}
                                    className="rounded-lg border border-slate-400 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                >
                                    View Board Queue
                                </button>
                            </>
                        )}
                        {status === 'SUBMITTED' && metadata?.version === history.at(-1)?.version && (
                            <button type="button" disabled={saving} onClick={revise} className="rounded-lg border border-slate-400 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">Create Revision</button>
                        )}
                        {(isCreateMode || status === 'DRAFT') && (
                            <button
                                type="submit"
                                disabled={saving}
                                className="rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-50"
                            >
                                {isCreateMode ? 'Create Draft' : 'Save Draft'}
                            </button>
                        )}
                        {!isCreateMode && status === 'DRAFT' && (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => transition('READY_FOR_REVIEW')}
                                className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50"
                            >
                                Move to Ready for Review
                            </button>
                        )}
                        {!isCreateMode && status === 'READY_FOR_REVIEW' && (
                            <button
                                type="button"
                                disabled={saving}
                                onClick={() => transition('SUBMITTED')}
                                className="rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                            >
                                Submit for Board Decision
                            </button>
                        )}
                    </div>
                </form>

                {!isCreateMode && (
                    <section className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-widest text-sky-700">Audit trail</p>
                        <h2 className="mt-1 text-lg font-semibold text-slate-900">Version History</h2>
                        <div className="mt-4 divide-y divide-slate-200">
                            {history.map((item) => (
                                <button key={item.version} type="button" onClick={() => navigate(`/capital-allocation-proposals/${item.proposal_id}/versions/${item.version}`)} className="grid w-full gap-2 py-3 text-left text-sm sm:grid-cols-5">
                                    <strong>Version {item.version}</strong><span>{item.status.replaceAll('_', ' ')}</span>
                                    <span>Prepared by {item.created_by}</span><span>{new Date(item.created_at).toLocaleString()}</span>
                                    <span>{item.revision_reason || 'Original proposal'}</span>
                                </button>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </main>
    )
}
